import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertCircle, ChevronRight, Download } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { SortableTable, type SortableColumn } from '@/components/ui/sortable-table';
import { StatTile } from '@/components/ui/stat-tile';
import { useBusinesses } from '@/features/business/use-businesses';
import { useDepartments } from '@/features/catalog/catalog.api';
import { saveAsFile } from '@/lib/download';
import { formatCurrency, formatNumber, toPercent } from '@/lib/format';
import { storeToday } from '@/lib/store-time';
import { MovementItems } from './movement-items';
import {
  isRunnable,
  useMovement,
  useMovementItems,
  type MovementLevel,
  type MovementQuery,
  type MovementRow,
} from './movement.api';

/**
 * Movement — one report, four levels, replacing four pages.
 *
 * `/finance/divisions`, `/finance/departments`, `/finance/store/departments`
 * and `/finance/store/categories` were 3,372 lines of the same page pasted
 * four times, differing in one endpoint prefix and one column heading. The
 * level is a filter, so it is a filter.
 *
 * Two things this page is careful about, both of which the originals got
 * wrong or left unsaid:
 *
 * **It says which number reconciles with the dashboard.** `Merchandise` is
 * `price × amount` over regular lines and is computed identically by
 * `trends/categories`, so the two pages agree on it for the same range and
 * stores. `Total` adds tax and modifier revenue, and the dashboard counts
 * neither — it filters `Type == Regular`. Showing both, with the modifier
 * column between them, turns "the reports disagree" into a subtraction anyone
 * can do.
 *
 * **The range is sent raw.** These v1 endpoints do
 * `EndDate.Date.AddDays(1)`, so the last picked day is already included; the
 * dashboard's v2 endpoints do `EndDate.Date.AddSeconds(-1)` and need a day
 * added. Adding one here too would silently report a day more than the
 * dashboard for the same picked range.
 */

const ANY = 'ANY';

/**
 * "No department picked yet" for the category level's picker.
 *
 * A sentinel rather than an empty string because Radix refuses a `SelectItem`
 * whose value is `''` — it reserves that for "nothing is selected".
 */
const NO_DEPARTMENT = 'NONE';

const LEVELS: MovementLevel[] = ['division', 'group', 'department', 'category'];

/**
 * The level a `?level=` in the URL asks for.
 *
 * Read once, on mount. It exists so the four routes this page replaces can
 * redirect to the level their old bookmark meant instead of dumping everyone
 * on Division — `/finance/store/categories` asked for categories, and should
 * still get them.
 */
function initialLevel(): MovementLevel {
  const asked = new URLSearchParams(window.location.search).get('level');
  return LEVELS.includes(asked as MovementLevel)
    ? (asked as MovementLevel)
    : 'division';
}

export function MovementPage() {
  const { t, i18n } = useTranslation();
  const spanish = (i18n.resolvedLanguage ?? 'en') === 'es';

  const today = storeToday();
  const [level, setLevel] = useState<MovementLevel>(initialLevel);
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [businessId, setBusinessId] = useState(ANY);

  /** Context for the category level, set by the picker or by a drill-down. */
  const [department, setDepartment] = useState<{
    id: string;
    label: string;
  } | null>(null);
  /** True when the category list was reached by clicking a department row. */
  const [fromDepartment, setFromDepartment] = useState(false);
  /** Non-null means the item list for one row is on screen. */
  const [drill, setDrill] = useState<{
    filter: string | null;
    label: string;
  } | null>(null);

  const { data: businesses } = useBusinesses();
  const { data: departments } = useDepartments();

  const query: MovementQuery = useMemo(
    () => ({
      level,
      start,
      end,
      businessId: businessId === ANY ? null : businessId,
      departmentId: department?.id ?? null,
    }),
    [level, start, end, businessId, department]
  );

  const report = useMovement(
    query,
    { fallback: t('movement.unnamed'), substituted: t('movement.substituted') },
    spanish
  );
  const items = useMovementItems(query, drill?.filter ?? null, drill !== null);

  const rows = report.data?.rows ?? [];

  /** Resetting the drill on any filter change — the row it named may be gone. */
  function change<T>(setter: (value: T) => void) {
    return (value: T) => {
      setter(value);
      setDrill(null);
    };
  }

  function changeLevel(next: string) {
    setLevel(next as MovementLevel);
    setDrill(null);
    // A department chosen by drilling belongs to that drill; one chosen from
    // the picker is the reader's own selection and survives a level change so
    // they can go back to it.
    if (fromDepartment) setDepartment(null);
    setFromDepartment(false);
  }

  /**
   * Down one level.
   *
   * Department is the only grouping with something between it and the
   * products — its categories — so it is the only one that moves the level
   * rather than opening the item list.
   */
  function openRow(row: MovementRow) {
    if (!row.filter) return;
    if (level === 'department') {
      setDepartment({ id: row.filter, label: row.label });
      setFromDepartment(true);
      setLevel('category');
      setDrill(null);
      return;
    }
    setDrill({ filter: row.filter, label: row.label });
  }

  const fields: FilterField[] = [
    {
      id: 'level',
      label: t('movement.level'),
      value: level,
      onChange: changeLevel,
      sort: false,
      resetValue: 'division',
      options: LEVELS.map((value) => ({
        value,
        label: t(`movement.level_${value}`),
      })),
    },
    {
      id: 'store',
      label: t('movement.store'),
      value: businessId,
      onChange: change(setBusinessId),
      options: [
        { value: ANY, label: t('movement.allStores') },
        ...(businesses ?? []).map((business) => ({
          value: business.id,
          label: business.name,
        })),
      ],
    },
    {
      id: 'start',
      label: t('movement.from'),
      type: 'date',
      value: start,
      resetValue: start,
      onChange: change(setStart),
    },
    {
      id: 'end',
      label: t('movement.to'),
      type: 'date',
      value: end,
      resetValue: end,
      onChange: change(setEnd),
    },
  ];

  // Categories only exist inside a department, so the picker is part of the
  // query at that level rather than an optional extra filter.
  if (level === 'category') {
    fields.splice(1, 0, {
      id: 'department',
      label: t('movement.department'),
      value: department?.id ?? NO_DEPARTMENT,
      resetValue: department?.id ?? NO_DEPARTMENT,
      onChange: (value) => {
        const found = (departments ?? []).find((entry) => entry.id === value);
        setDepartment(
          found
            ? {
                id: found.id,
                label:
                  (spanish ? found.name : found.nameEng) ||
                  found.nameEng ||
                  found.name ||
                  t('movement.unnamed'),
              }
            : null
        );
        setFromDepartment(false);
        setDrill(null);
      },
      options: [
        { value: NO_DEPARTMENT, label: t('movement.pickDepartment') },
        ...(departments ?? []).map((entry) => ({
          value: entry.id,
          label:
            (spanish ? entry.name : entry.nameEng) ||
            entry.nameEng ||
            entry.name ||
            t('movement.unnamed'),
        })),
      ],
    });
  }

  // ------------------------------------------------------------ breadcrumb

  const crumbs: { label: string; onClick?: () => void }[] = [];
  if (fromDepartment) {
    crumbs.push({
      label: t('movement.level_department'),
      onClick: () => {
        setLevel('department');
        setDepartment(null);
        setFromDepartment(false);
        setDrill(null);
      },
    });
  }
  crumbs.push({
    label:
      fromDepartment && department
        ? department.label
        : t(`movement.level_${level}`),
    onClick: drill ? () => setDrill(null) : undefined,
  });
  if (drill) crumbs.push({ label: drill.label });

  // ---------------------------------------------------------------- totals

  const tax = rows.reduce((sum, row) => sum + row.stateTax + row.municipalTax, 0);
  const modifiers = rows.reduce((sum, row) => sum + row.modifiers, 0);

  // ---------------------------------------------------------------- export

  function runExport() {
    const escape = (value: unknown) =>
      `"${String(value ?? '').replace(/"/g, '""')}"`;
    const name = `movement-${level}-${start}-to-${end}.csv`;

    if (drill) {
      const list = items.data ?? [];
      if (!list.length) {
        toast.info(t('movement.nothingToExport'));
        return;
      }
      const csv = [
        [
          t('movement.upc'),
          t('movement.item'),
          t('movement.units'),
          t('movement.priceTotal'),
          t('movement.stateTax'),
          t('movement.municipalTax'),
          t('movement.modifiers'),
          t('movement.totalSales'),
          t('movement.avgCost'),
          t('movement.profit'),
          t('movement.margin'),
        ].join(','),
        ...list.map((item) =>
          [
            item.upc,
            [item.brand, item.name, item.description]
              .filter((part) => part?.trim())
              .join(' '),
            item.amount,
            item.priceTotal,
            item.stateTaxTotal,
            item.municipalTaxTotal,
            item.modifierSubtotal +
              item.modifierStateTax +
              item.modifierMunicipalTax,
            item.totalSales,
            item.cost,
            item.profit,
            toPercent(item.profitMargin),
          ]
            .map(escape)
            .join(',')
        ),
      ].join('\n');
      saveAsFile(csv, name, 'text/csv;charset=utf-8');
      toast.success(t('movement.exported', { count: list.length }));
      return;
    }

    if (!rows.length) {
      toast.info(t('movement.nothingToExport'));
      return;
    }
    const csv = [
      [
        t(`movement.level_${level}`),
        t('movement.units'),
        t('movement.merchandise'),
        t('movement.stateTax'),
        t('movement.municipalTax'),
        t('movement.modifiers'),
        t('movement.total'),
        t('movement.profit'),
        t('movement.margin'),
      ].join(','),
      ...rows.map((row) =>
        [
          row.label,
          row.units,
          row.extended,
          row.stateTax,
          row.municipalTax,
          row.modifiers,
          row.total,
          row.profit,
          toPercent(row.profitMargin),
        ]
          .map(escape)
          .join(',')
      ),
    ].join('\n');
    saveAsFile(csv, name, 'text/csv;charset=utf-8');
    toast.success(t('movement.exported', { count: rows.length }));
  }

  // --------------------------------------------------------------- columns

  const merchandise = report.data?.merchandise ?? 0;

  const columns: SortableColumn<MovementRow>[] = [
    {
      key: 'label',
      header: t(`movement.level_${level}`),
      sortValue: (row) => row.label,
      render: (row) => (
        <span className="flex items-center gap-1 font-medium">
          {row.label}
          {row.filter && (
            <ChevronRight className="text-muted-foreground size-3.5" />
          )}
        </span>
      ),
    },
    {
      key: 'units',
      header: t('movement.units'),
      align: 'right',
      sortValue: (row) => row.units,
      render: (row) => formatNumber(row.units),
    },
    {
      key: 'merchandise',
      header: t('movement.merchandise'),
      align: 'right',
      sortValue: (row) => row.extended,
      render: (row) => formatCurrency(row.extended),
    },
    {
      key: 'share',
      header: t('movement.share'),
      align: 'right',
      sortValue: (row) => row.extended,
      render: (row) =>
        merchandise > 0
          ? `${((row.extended / merchandise) * 100).toFixed(2)}%`
          : '—',
    },
    {
      key: 'modifiers',
      header: t('movement.modifiers'),
      align: 'right',
      sortValue: (row) => row.modifiers,
      render: (row) => (row.modifiers ? formatCurrency(row.modifiers) : '—'),
    },
    {
      key: 'total',
      header: t('movement.total'),
      align: 'right',
      sortValue: (row) => row.total,
      render: (row) => formatCurrency(row.total),
    },
    {
      key: 'profit',
      header: t('movement.profit'),
      align: 'right',
      sortValue: (row) => row.profit,
      render: (row) => formatCurrency(row.profit),
    },
    {
      key: 'margin',
      header: t('movement.margin'),
      align: 'right',
      sortValue: (row) => row.profitMargin,
      render: (row) => `${toPercent(row.profitMargin).toFixed(1)}%`,
    },
  ];

  const needsDepartment = !isRunnable(query);
  const busy = drill ? items.isFetching : report.isFetching;
  const error = drill ? items.error : report.error;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('movement.title')}
        description={t('movement.subtitle')}
        actions={
          <Button
            variant="outline"
            size="sm"
            disabled={busy || needsDepartment}
            onClick={runExport}
          >
            <Download className="size-3.5" />
            <span className="hidden sm:inline">{t('movement.export')}</span>
          </Button>
        }
      />

      <FilterBar busy={busy} fields={fields} />

      {needsDepartment ? (
        <p className="text-muted-foreground rounded-xl border border-dashed py-10 text-center text-sm">
          {t('movement.pickDepartmentFirst')}
        </p>
      ) : (
        <>
          {/* ---------------------------------------------------- totals */}
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
            <StatTile
              label={t('movement.orders')}
              value={formatNumber(report.data?.orders ?? 0)}
              tooltip={t('movement.ordersHint')}
              loading={report.isPending}
              busy={report.isFetching}
            />
            <StatTile
              label={t('movement.lines')}
              value={formatNumber(report.data?.lines ?? 0)}
              tooltip={t('movement.linesHint')}
              loading={report.isPending}
              busy={report.isFetching}
            />
            <StatTile
              label={t('movement.unitsSold')}
              value={formatNumber(report.data?.units ?? 0)}
              tooltip={t('movement.unitsHint')}
              loading={report.isPending}
              busy={report.isFetching}
            />
            <StatTile
              label={t('movement.merchandise')}
              value={formatCurrency(merchandise)}
              tone="cash"
              tooltip={t('movement.merchandiseHint')}
              loading={report.isPending}
              busy={report.isFetching}
            />
            <StatTile
              label={t('movement.total')}
              value={formatCurrency(report.data?.total ?? 0)}
              tone="primary"
              sub={t('movement.totalBreakdown', {
                tax: formatCurrency(tax),
                modifiers: formatCurrency(modifiers),
              })}
              tooltip={t('movement.totalHint')}
              loading={report.isPending}
              busy={report.isFetching}
            />
          </div>

          {/* ------------------------------------------------ breadcrumb */}
          <nav
            aria-label={t('movement.breadcrumb')}
            className="flex flex-wrap items-center gap-1 text-sm"
          >
            {crumbs.map((crumb, index) => (
              <span key={`${crumb.label}-${index}`} className="flex items-center gap-1">
                {index > 0 && (
                  <ChevronRight className="text-muted-foreground size-3.5" />
                )}
                {crumb.onClick ? (
                  <button
                    type="button"
                    onClick={crumb.onClick}
                    className="text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                  >
                    {crumb.label}
                  </button>
                ) : (
                  <span className="font-medium">{crumb.label}</span>
                )}
              </span>
            ))}
            {!drill && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto"
                disabled={report.isPending}
                onClick={() =>
                  setDrill({ filter: null, label: t('movement.allItems') })
                }
              >
                {t('movement.viewAllItems')}
              </Button>
            )}
          </nav>

          {error && (
            <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
              <AlertCircle className="mt-0.5 size-4 shrink-0" />
              <span>
                {error instanceof Error ? error.message : t('movement.failed')}
              </span>
            </div>
          )}

          {drill ? (
            <MovementItems
              items={items.data ?? []}
              loading={items.isPending}
              busy={items.isFetching}
              emptyMessage={t('movement.noItems')}
            />
          ) : (
            <SortableTable
              rows={rows}
              columns={columns}
              rowKey={(row) => row.filter ?? `unnamed-${row.label}`}
              loading={report.isPending}
              busy={report.isFetching}
              pageSize={25}
              initialSort={{ key: 'merchandise', direction: 'desc' }}
              emptyMessage={t('movement.noRows')}
              onRowClick={openRow}
              rowClickable={(row) => Boolean(row.filter)}
            />
          )}
        </>
      )}
    </div>
  );
}
